import type { LandingPage, LandingPageSection, Persona, Product } from "@shared/schema";

const SECTION_LABELS: Record<string, string> = {
  announcement_bar: "Announcement Bar",
  hero_buy_box: "Hero / Buy Box",
  sticky_buy_bar: "Sticky Buy Bar",
  product_recipe: "Product Cards & Recipe",
  how_to_use: "How to Use",
  science_research: "Science & Research",
  belief_shift_competitor: "Belief Shift / Competitor",
  farmstand_story: "Origin Story",
  timeline_results: "Timeline & Results",
  quiz_funnel: "Quiz Funnel",
  reviews_grid: "Reviews",
  faq_accordion: "FAQ",
  final_cta: "Final CTA",
};

interface PreviewProps {
  page: LandingPage;
  sections: LandingPageSection[];
  persona: Persona | null;
  product: Product | null;
  brandName: string;
}

function ov(section: LandingPageSection): Record<string, any> {
  return (section.overrides as Record<string, any>) || {};
}

function offerData(page: LandingPage): Record<string, any> {
  return (page.offerData as Record<string, any>) || {};
}

function bullets(val: any): string[] {
  if (Array.isArray(val)) return val.filter(Boolean);
  if (typeof val === "string") return val.split("\n").filter(Boolean);
  return [];
}

function AnnouncementBar({ section }: { section: LandingPageSection }) {
  const o = ov(section);
  const items = o.headline || "Always handmade · Always small-batch · Always shipped fresh";
  return (
    <div style={{ background: "#1a2332", color: "#fff", padding: "10px 0", overflow: "hidden", fontSize: 13, letterSpacing: "0.02em" }}>
      <div style={{ display: "flex", justifyContent: "center", gap: 24, whiteSpace: "nowrap", animation: "none" }}>
        {items.split("·").map((item: string, i: number) => (
          <span key={i} style={{ opacity: 0.9 }}>
            {i > 0 && <span style={{ margin: "0 8px", opacity: 0.4 }}>·</span>}
            {item.trim()}
          </span>
        ))}
      </div>
    </div>
  );
}

function HeroBuyBox({ section, page, brandName }: { section: LandingPageSection; page: LandingPage; brandName: string }) {
  const o = ov(section);
  const offer = offerData(page);
  const price = offer.priceToday || "";
  const retail = offer.retailValue || "";
  const savings = offer.savingsAmount || (retail && price ? (parseFloat(retail) - parseFloat(price)).toFixed(0) : "");
  const discount = offer.discountPercent || "";
  const bul = bullets(o.bullets);

  return (
    <div style={{ background: "linear-gradient(135deg, #f8f6f0 0%, #f0ece0 100%)", padding: "60px 24px", textAlign: "center" }}>
      <div style={{ maxWidth: 680, margin: "0 auto" }}>
        {discount && (
          <div style={{ display: "inline-block", background: "#c53030", color: "#fff", padding: "6px 16px", borderRadius: 20, fontSize: 13, fontWeight: 700, marginBottom: 16, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            {discount}% OFF — SPRING SALE
          </div>
        )}
        <h1 style={{ fontSize: 36, fontWeight: 900, lineHeight: 1.15, color: "#1a2332", margin: "0 0 12px" }}>
          {o.headline || `${brandName} — Your New Daily Routine`}
        </h1>
        <p style={{ fontSize: 17, color: "#555", lineHeight: 1.5, margin: "0 0 20px" }}>
          {o.subheadline || "Everything your skin needs. Nothing it doesn't."}
        </p>
        {o.body && <p style={{ fontSize: 14, color: "#666", margin: "0 0 16px", lineHeight: 1.6 }}>{o.body}</p>}

        <div style={{ display: "flex", justifyContent: "center", alignItems: "baseline", gap: 12, margin: "20px 0" }}>
          {price && <span style={{ fontSize: 42, fontWeight: 900, color: "#1a2332" }}>${price}</span>}
          {retail && <span style={{ fontSize: 20, color: "#999", textDecoration: "line-through" }}>${retail}</span>}
          {savings && <span style={{ fontSize: 14, color: "#c53030", fontWeight: 600 }}>Save ${savings}</span>}
        </div>

        {bul.length > 0 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap", margin: "16px 0" }}>
            {bul.map((b, i) => (
              <span key={i} style={{ fontSize: 13, color: "#555", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: "#38a169", fontSize: 14 }}>✓</span> {b}
              </span>
            ))}
          </div>
        )}

        <button style={{
          display: "inline-block", background: "#c53030", color: "#fff", border: "none",
          padding: "16px 48px", borderRadius: 8, fontSize: 16, fontWeight: 700,
          cursor: "pointer", marginTop: 20, letterSpacing: "0.02em",
        }}>
          {o.ctaText || "Shop Now"}
        </button>

        <div style={{ marginTop: 14, fontSize: 12, color: "#888" }}>
          {offer.shippingOffer || "Free shipping"} · {offer.guaranteeDays || 45}-day guarantee
        </div>
      </div>
    </div>
  );
}

function StickyBuyBar({ section, page, brandName }: { section: LandingPageSection; page: LandingPage; brandName: string }) {
  const o = ov(section);
  const offer = offerData(page);
  return (
    <div style={{ background: "#fff", borderBottom: "1px solid #e2e2e2", padding: "10px 24px", display: "flex", justifyContent: "center", alignItems: "center", gap: 20 }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: "#1a2332" }}>
        {o.headline || brandName}
      </span>
      {offer.priceToday && (
        <span style={{ fontSize: 14, color: "#555" }}>
          <strong>${offer.priceToday}</strong>
          {offer.retailValue && <span style={{ textDecoration: "line-through", color: "#999", marginLeft: 6, fontSize: 12 }}>${offer.retailValue}</span>}
        </span>
      )}
      <button style={{ background: "#c53030", color: "#fff", border: "none", padding: "8px 20px", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
        {o.ctaText || "Order Now"}
      </button>
    </div>
  );
}

function ProductRecipe({ section }: { section: LandingPageSection }) {
  const o = ov(section);
  const bul = bullets(o.bullets);
  return (
    <div style={{ background: "#fff", padding: "56px 24px" }}>
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a2332", textAlign: "center", marginBottom: 8 }}>
          {o.headline || "What's Inside"}
        </h2>
        <p style={{ fontSize: 15, color: "#666", textAlign: "center", marginBottom: 32 }}>
          {o.subheadline || "Each product works together as a complete daily routine."}
        </p>
        {o.body && <p style={{ fontSize: 14, color: "#555", lineHeight: 1.7, textAlign: "center", margin: "0 auto 24px", maxWidth: 600 }}>{o.body}</p>}
        {bul.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
            {bul.map((b, i) => (
              <div key={i} style={{ background: "#fafaf8", border: "1px solid #eee", borderRadius: 12, padding: "20px 18px" }}>
                <p style={{ fontSize: 14, color: "#333", lineHeight: 1.5 }}>{b}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HowToUse({ section }: { section: LandingPageSection }) {
  const o = ov(section);
  const bul = bullets(o.bullets);
  return (
    <div style={{ background: "#fafaf8", padding: "56px 24px" }}>
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a2332", textAlign: "center", marginBottom: 8 }}>
          {o.headline || "How to Use"}
        </h2>
        <p style={{ fontSize: 15, color: "#666", textAlign: "center", marginBottom: 32 }}>
          {o.subheadline || "A simple daily routine. No complicated steps."}
        </p>
        {bul.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {bul.map((b, i) => (
              <div key={i} style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#1a2332", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                  {i + 1}
                </div>
                <p style={{ fontSize: 14, color: "#444", lineHeight: 1.6, paddingTop: 7 }}>{b}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ScienceResearch({ section }: { section: LandingPageSection }) {
  const o = ov(section);
  const bul = bullets(o.bullets);
  return (
    <div style={{ background: "#fff", padding: "56px 24px" }}>
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a2332", textAlign: "center", marginBottom: 8 }}>
          {o.headline || "Backed by Research"}
        </h2>
        <p style={{ fontSize: 15, color: "#666", textAlign: "center", marginBottom: 32 }}>
          {o.subheadline || "Ingredients researched from peer-reviewed journals."}
        </p>
        {bul.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {bul.map((b, i) => (
              <div key={i} style={{ background: "#f8faf8", border: "1px solid #e2ede2", borderRadius: 10, padding: "16px 18px", display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ color: "#38a169", fontSize: 18, flexShrink: 0, marginTop: 1 }}>🔬</span>
                <p style={{ fontSize: 14, color: "#444", lineHeight: 1.6 }}>{b}</p>
              </div>
            ))}
          </div>
        )}
        {o.body && <p style={{ fontSize: 11, color: "#999", textAlign: "center", marginTop: 24, lineHeight: 1.5 }}>{o.body}</p>}
      </div>
    </div>
  );
}

function BeliefShiftCompetitor({ section }: { section: LandingPageSection }) {
  const o = ov(section);
  const bul = bullets(o.bullets);
  return (
    <div style={{ background: "#1a2332", color: "#fff", padding: "56px 24px" }}>
      <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 8, lineHeight: 1.2 }}>
          {o.headline || "What They Told You vs. What's Actually True"}
        </h2>
        <p style={{ fontSize: 16, color: "rgba(255,255,255,0.7)", marginBottom: 32, lineHeight: 1.6 }}>
          {o.subheadline || "The skincare industry optimized for shareholders, not your skin."}
        </p>
        {o.body && <p style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", lineHeight: 1.7, marginBottom: 24 }}>{o.body}</p>}
        {bul.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, textAlign: "left" }}>
            {bul.map((b, i) => (
              <div key={i} style={{ background: "rgba(255,255,255,0.08)", borderRadius: 10, padding: "16px 18px", border: "1px solid rgba(255,255,255,0.1)" }}>
                <p style={{ fontSize: 14, color: "rgba(255,255,255,0.85)", lineHeight: 1.5 }}>{b}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FarmstandStory({ section }: { section: LandingPageSection }) {
  const o = ov(section);
  const bul = bullets(o.bullets);
  return (
    <div style={{ background: "#f8f6f0", padding: "56px 24px" }}>
      <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a2332", marginBottom: 8 }}>
          {o.headline || "Where It's Made"}
        </h2>
        <p style={{ fontSize: 16, color: "#555", lineHeight: 1.6, marginBottom: 24 }}>
          {o.subheadline || "Real place. Real people. Made fresh, not warehoused."}
        </p>
        {o.body && <p style={{ fontSize: 14, color: "#666", lineHeight: 1.7, marginBottom: 24, maxWidth: 560, marginLeft: "auto", marginRight: "auto" }}>{o.body}</p>}
        {bul.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, textAlign: "left", maxWidth: 500, margin: "0 auto" }}>
            {bul.map((b, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{ color: "#38a169", fontSize: 14, marginTop: 2, flexShrink: 0 }}>✓</span>
                <p style={{ fontSize: 14, color: "#555", lineHeight: 1.5 }}>{b}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineResults({ section }: { section: LandingPageSection }) {
  const o = ov(section);
  const bul = bullets(o.bullets);
  const steps = bul.length > 0 ? bul : ["Week 1: Softer, more hydrated skin", "Week 2-3: Reduced redness, smoother texture", "Month 2: Visible transformation"];
  return (
    <div style={{ background: "#fff", padding: "56px 24px" }}>
      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a2332", textAlign: "center", marginBottom: 8 }}>
          {o.headline || "What to Expect"}
        </h2>
        <p style={{ fontSize: 15, color: "#666", textAlign: "center", marginBottom: 32 }}>
          {o.subheadline || "Real results take consistency. Here's the typical timeline."}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 0, position: "relative", paddingLeft: 24 }}>
          <div style={{ position: "absolute", left: 10, top: 8, bottom: 8, width: 2, background: "#e0e0e0" }} />
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 16, alignItems: "flex-start", paddingBottom: 24, position: "relative" }}>
              <div style={{ width: 14, height: 14, borderRadius: "50%", background: i === steps.length - 1 ? "#38a169" : "#1a2332", flexShrink: 0, position: "absolute", left: -20, top: 4, border: "2px solid #fff", boxShadow: "0 0 0 2px #e0e0e0" }} />
              <p style={{ fontSize: 14, color: "#444", lineHeight: 1.6, marginLeft: 8 }}>{s}</p>
            </div>
          ))}
        </div>
        {o.body && <p style={{ fontSize: 13, color: "#888", textAlign: "center", marginTop: 12 }}>{o.body}</p>}
      </div>
    </div>
  );
}

function QuizFunnel({ section }: { section: LandingPageSection }) {
  const o = ov(section);
  return (
    <div style={{ background: "linear-gradient(135deg, #f0ece0 0%, #e8e0d0 100%)", padding: "56px 24px", textAlign: "center" }}>
      <div style={{ maxWidth: 500, margin: "0 auto" }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a2332", marginBottom: 8 }}>
          {o.headline || "Find Your Perfect Routine"}
        </h2>
        <p style={{ fontSize: 15, color: "#555", lineHeight: 1.5, marginBottom: 24 }}>
          {o.subheadline || "Answer a few quick questions and we'll personalize your results."}
        </p>
        <div style={{ background: "#fff", borderRadius: 16, padding: "32px 24px", border: "1px solid #e2dcd0", boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
          <p style={{ fontSize: 14, color: "#888", marginBottom: 16 }}>Interactive Quiz Preview</p>
          <div style={{ background: "#f5f5f5", borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <p style={{ fontSize: 14, color: "#555" }}>{o.body || "What's your biggest skin concern?"}</p>
          </div>
          <button style={{ background: "#1a2332", color: "#fff", border: "none", padding: "12px 32px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            {o.ctaText || "Take the Quiz"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewsGrid({ section }: { section: LandingPageSection }) {
  const o = ov(section);
  const bul = bullets(o.bullets);
  const reviews = bul.length > 0 ? bul : [
    "Best product I've ever used. My skin has completely transformed.",
    "After one week the dry skin is gone. I tell everyone about it.",
    "Using it at night has transformed the skin around my eyes. Superb!",
  ];
  return (
    <div style={{ background: "#fafaf8", padding: "56px 24px" }}>
      <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center" }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a2332", marginBottom: 4 }}>
          {o.headline || "What Customers Say"}
        </h2>
        <p style={{ fontSize: 15, color: "#888", marginBottom: 32 }}>
          {o.subheadline || "⭐ 4.8 · Based on thousands of verified reviews"}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
          {reviews.map((r, i) => (
            <div key={i} style={{ background: "#fff", borderRadius: 12, padding: "20px 18px", border: "1px solid #eee", textAlign: "left" }}>
              <div style={{ color: "#f5a623", fontSize: 14, marginBottom: 8 }}>★★★★★</div>
              <p style={{ fontSize: 14, color: "#444", lineHeight: 1.6, fontStyle: "italic" }}>"{r}"</p>
              <p style={{ fontSize: 12, color: "#aaa", marginTop: 10 }}>Verified Buyer</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FaqAccordion({ section }: { section: LandingPageSection }) {
  const o = ov(section);
  const bul = bullets(o.bullets);
  const items = bul.length > 0 ? bul : [
    "Q: How long until I see results? A: Most notice softer skin within the first week.",
    "Q: Is this safe for sensitive skin? A: Yes — whole-food ingredients, no harsh chemicals.",
    "Q: What's the return policy? A: 45-day money-back guarantee, no questions asked.",
  ];
  return (
    <div style={{ background: "#fff", padding: "56px 24px" }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a2332", textAlign: "center", marginBottom: 32 }}>
          {o.headline || "Frequently Asked Questions"}
        </h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {items.map((item, i) => {
            const parts = item.split(/\bA:\s*/);
            const q = parts[0]?.replace(/^Q:\s*/, "").trim() || item;
            const a = parts[1]?.trim() || "";
            return (
              <div key={i} style={{ borderBottom: "1px solid #eee", padding: "18px 0" }}>
                <p style={{ fontSize: 15, fontWeight: 600, color: "#1a2332", marginBottom: a ? 8 : 0 }}>{q}</p>
                {a && <p style={{ fontSize: 14, color: "#666", lineHeight: 1.6 }}>{a}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FinalCta({ section, page }: { section: LandingPageSection; page: LandingPage }) {
  const o = ov(section);
  const offer = offerData(page);
  const bul = bullets(o.bullets);
  return (
    <div style={{ background: "linear-gradient(135deg, #1a2332 0%, #2d3748 100%)", color: "#fff", padding: "64px 24px", textAlign: "center" }}>
      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        <h2 style={{ fontSize: 32, fontWeight: 900, marginBottom: 12, lineHeight: 1.2 }}>
          {o.headline || "Don't Wait — This Offer Won't Last"}
        </h2>
        <p style={{ fontSize: 16, color: "rgba(255,255,255,0.7)", marginBottom: 24, lineHeight: 1.5 }}>
          {o.subheadline || "Your skin deserves better. Start today."}
        </p>

        <div style={{ display: "flex", justifyContent: "center", alignItems: "baseline", gap: 12, marginBottom: 20 }}>
          {offer.priceToday && <span style={{ fontSize: 42, fontWeight: 900 }}>${offer.priceToday}</span>}
          {offer.retailValue && <span style={{ fontSize: 20, color: "rgba(255,255,255,0.5)", textDecoration: "line-through" }}>${offer.retailValue}</span>}
        </div>

        {bul.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center", marginBottom: 24 }}>
            {bul.map((b, i) => (
              <span key={i} style={{ fontSize: 14, color: "rgba(255,255,255,0.8)", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: "#68d391" }}>✓</span> {b}
              </span>
            ))}
          </div>
        )}

        <button style={{
          display: "inline-block", background: "#c53030", color: "#fff", border: "none",
          padding: "16px 48px", borderRadius: 8, fontSize: 16, fontWeight: 700,
          cursor: "pointer", letterSpacing: "0.02em",
        }}>
          {o.ctaText || "Order Now"}
        </button>

        <div style={{ marginTop: 16, fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
          {offer.shippingOffer || "Free shipping"} · {offer.guaranteeDays || 45}-day money-back guarantee
        </div>

        {offer.urgencyLabel && (
          <div style={{ marginTop: 12, fontSize: 13, color: "#f5a623", fontWeight: 600 }}>
            {offer.urgencyLabel}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptySection({ sectionType }: { sectionType: string }) {
  const label = SECTION_LABELS[sectionType] || sectionType;
  return (
    <div style={{ padding: "40px 24px", textAlign: "center", border: "1px dashed #ddd", margin: "0" }}>
      <p style={{ fontSize: 14, color: "#aaa" }}>{label}</p>
      <p style={{ fontSize: 12, color: "#ccc", marginTop: 4 }}>Add content in the Sections tab</p>
    </div>
  );
}

const RENDERERS: Record<string, (props: { section: LandingPageSection; page: LandingPage; brandName: string }) => JSX.Element> = {
  announcement_bar: (p) => <AnnouncementBar section={p.section} />,
  hero_buy_box: (p) => <HeroBuyBox section={p.section} page={p.page} brandName={p.brandName} />,
  sticky_buy_bar: (p) => <StickyBuyBar section={p.section} page={p.page} brandName={p.brandName} />,
  product_recipe: (p) => <ProductRecipe section={p.section} />,
  how_to_use: (p) => <HowToUse section={p.section} />,
  science_research: (p) => <ScienceResearch section={p.section} />,
  belief_shift_competitor: (p) => <BeliefShiftCompetitor section={p.section} />,
  farmstand_story: (p) => <FarmstandStory section={p.section} />,
  timeline_results: (p) => <TimelineResults section={p.section} />,
  quiz_funnel: (p) => <QuizFunnel section={p.section} />,
  reviews_grid: (p) => <ReviewsGrid section={p.section} />,
  faq_accordion: (p) => <FaqAccordion section={p.section} />,
  final_cta: (p) => <FinalCta section={p.section} page={p.page} />,
};

export default function LandingPagePreview({ page, sections, brandName }: PreviewProps) {
  const enabledSections = sections.filter(s => s.enabled).sort((a, b) => a.order - b.order);

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", color: "#1a2332", background: "#fff", minHeight: "100%" }}>
      {enabledSections.map(section => {
        const Renderer = RENDERERS[section.sectionType];
        if (Renderer) {
          return <div key={section.id}>{Renderer({ section, page, brandName })}</div>;
        }
        return <EmptySection key={section.id} sectionType={section.sectionType} />;
      })}
      {enabledSections.length === 0 && (
        <div style={{ padding: "80px 24px", textAlign: "center" }}>
          <p style={{ fontSize: 16, color: "#999" }}>No sections enabled</p>
          <p style={{ fontSize: 13, color: "#ccc", marginTop: 8 }}>Enable sections in the Sections tab to see the preview</p>
        </div>
      )}
    </div>
  );
}
